import React from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaCheckCircle, FaMapMarkerAlt, FaShieldAlt, FaStar } from "react-icons/fa";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  formatMetric,
  getCapacity,
  getCardImage,
  getCertificationShortLabels,
  getCompletedOrders,
  getIndustryTags,
  getManufacturerLocation,
  getMoqSummary,
  getRatingDisplay,
  getResponseTime,
  getReviewCount,
  isFeaturedManufacturer,
} from "../../utils/contractManufacturerCardHelpers";
import MarketplaceProductShareButton from "./MarketplaceProductShareButton";

function buildSpecItems(item, dimensionUnitOptions) {
  return [
    { label: "Capacity", value: formatMetric(getCapacity(item)) },
    { label: "Response", value: formatMetric(getResponseTime(item)) },
    { label: "Orders", value: formatMetric(getCompletedOrders(item)) },
    { label: "MOQ", value: formatMetric(getMoqSummary(item, dimensionUnitOptions)) },
  ];
}

function resolveBadge(item) {
  const isVerified = String(item?.verification_status || "").toLowerCase() === "verified";
  if (isVerified) {
    return { label: "VERIFIED", className: "marketplace-product-row__badge--certified" };
  }
  if (isFeaturedManufacturer(item)) {
    return { label: "FEATURED", className: "marketplace-product-row__badge--seller" };
  }
  return null;
}

function RatingStars({ item }) {
  const rating = getRatingDisplay(item);
  const reviews = getReviewCount(item);
  if (rating == null) return null;

  const numericRating = Number(rating);
  return (
    <div className="marketplace-product-row__rating">
      <span className="marketplace-product-row__stars" aria-hidden>
        {Array.from({ length: 5 }).map((_, index) => (
          <FaStar key={index} className={index + 1 <= Math.round(numericRating) ? "is-filled" : ""} />
        ))}
      </span>
      <span className="marketplace-product-row__rating-label">
        {rating}
        {reviews != null ? ` (${reviews})` : ""}
      </span>
    </div>
  );
}

export default function ContractManufacturerListRow({
  item,
  industryFilterOptions = [],
  dimensionUnitOptions = [],
  rfqSent = false,
  onToggleCompare,
  compared = false,
}) {
  const profileHref = `/contract-manufacturers/profile/${item.company_id}`;
  const rfqHref = `/contract-manufacturers/rfq/${item.company_id}`;
  const imageUrl = getCardImage(item);
  const location = getManufacturerLocation(item);
  const industryTrail = getIndustryTags(item, industryFilterOptions, 3);
  const specItems = buildSpecItems(item, dimensionUnitOptions);
  const certBadges = getCertificationShortLabels(item, 4);
  const badge = resolveBadge(item);
  const isVerified = String(item?.verification_status || "").toLowerCase() === "verified";
  const capacityLabel = formatMetric(getCapacity(item));

  return (
    <article className="marketplace-product-row marketplace-product-row--cm group">
      <div className="marketplace-product-row__media">
        {badge ? (
          <span className={`marketplace-product-row__badge ${badge.className}`}>{badge.label}</span>
        ) : null}
        <Link to={profileHref} className="marketplace-product-row__media-link">
          {imageUrl ? (
            <img src={resolvePublicFileUrl(imageUrl)} alt={item.company_name || "Manufacturer"} loading="lazy" />
          ) : (
            <div className="marketplace-product-row__no-image">No image</div>
          )}
        </Link>
      </div>

      <div className="marketplace-product-row__body">
        <div className="marketplace-product-row__head">
          <div className="marketplace-product-row__head-left">
            <Link to={profileHref} className="marketplace-product-row__title">
              {item.company_name || "Manufacturer"}
            </Link>

            {industryTrail.length ? (
              <p className="marketplace-product-row__category-trail">{industryTrail.join(" › ")}</p>
            ) : null}

            <div className="marketplace-product-row__seller">
              <span className="marketplace-product-row__company">Contract Manufacturer</span>
              {isVerified ? (
                <span className="marketplace-product-row__verified">
                  <FaCheckCircle aria-hidden /> VERIFIED
                </span>
              ) : null}
              {location ? (
                <span className="marketplace-product-row__location">
                  <FaMapMarkerAlt aria-hidden /> {location}
                </span>
              ) : null}
            </div>
          </div>

          <div className="marketplace-product-row__head-right">
            {capacityLabel !== "—" ? (
              <p className="marketplace-product-row__price">{capacityLabel}</p>
            ) : null}
            <RatingStars item={item} />
          </div>
        </div>

        <dl
          className="marketplace-product-row__specs"
          style={{ gridTemplateColumns: `repeat(${specItems.length}, minmax(0, 1fr))` }}
        >
          {specItems.map((spec) => (
            <div key={`${spec.label}-${spec.value}`} className="marketplace-product-row__spec">
              <dt>{spec.label}</dt>
              <dd>{spec.value}</dd>
            </div>
          ))}
        </dl>

        <div className={`marketplace-product-row__meta${certBadges.length ? "" : " marketplace-product-row__meta--empty"}`}>
          {certBadges.map((cert) => (
            <span key={cert} className="marketplace-product-row__cert">
              {cert}
            </span>
          ))}
        </div>

        <div className="marketplace-product-row__actions">
          {rfqSent ? (
            <button type="button" className="marketplace-product-row__btn marketplace-product-row__btn--primary" disabled>
              <FaShieldAlt aria-hidden /> RFQ Sent
            </button>
          ) : (
            <Link to={rfqHref} className="marketplace-product-row__btn marketplace-product-row__btn--primary">
              Request for Quote
            </Link>
          )}
          <Link to={profileHref} className="marketplace-product-row__btn marketplace-product-row__btn--outline-dark">
            View Profile
          </Link>
          <button
            type="button"
            className={`marketplace-product-row__btn marketplace-product-row__btn--outline${compared ? " is-active" : ""}`}
            onClick={() => onToggleCompare?.(item.company_id)}
          >
            <FaBalanceScale aria-hidden /> Compare
          </button>
          <MarketplaceProductShareButton
            productId={item.company_id}
            title={item.company_name || "Contract Manufacturer"}
            sharePath={profileHref}
            imageUrl={imageUrl ? resolvePublicFileUrl(imageUrl) : ""}
            companyName={item.company_name || ""}
            description={`Contract manufacturer profile${location ? ` — ${location}` : ""}`}
            triggerClassName="marketplace-product-row__btn marketplace-product-row__btn--outline marketplace-product-share__trigger"
          />
        </div>
      </div>
    </article>
  );
}
